import React, { memo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '../../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import {
    LARGE_HEIGHT,
    CARD_GAP,
    CARD_FONTS,
    CARD_ICONS,
    CARD_PADDING,
    CARD_RADIUS,
    CARD_SHADOW
} from './cardConstants';

// Ícones da fileira do card. Em migração (2026-08-09): os logos de órgãos/bancos
// (marcas de terceiros, sem relação com o acervo nem com o ENADE) estão sendo
// trocados, um a um, por símbolos de curso/profissão escolhidos pelo usuário.
// Dois padrões de nome convivem de propósito:
//   · `area_<id>.png`     — a arte representa UMA das 7 áreas de
//                           `src/constants/cursos.js` (o `<id>` casa com o de lá);
//   · `simbolo_<nome>.png` — arte adicional que não mapeia 1:1 numa área (ex.: um
//                           2º símbolo de Engenharias ou de Saúde). Decisão do
//                           usuário em 2026-08-09, ciente da sobreposição.
// FUNDO SEMPRE TRANSPARENTE (o card é escuro; PNG opaco vira quadrado dentro de
// quadrado). Cor: arte de traço simples é convertida para glifo BRANCO; arte
// cujo detalhe vive na cor mantém as cores originais — a silhueta dela viraria
// uma mancha ilegível a 22px (testado com o emblema de Engenharias).
// A fileira cresce conforme o usuário traz as artes; renderiza no máximo 8.
const ROW_ICONS = [
    require('../../../../assets/icons/area_saude.png'),               // Saúde
    require('../../../../assets/icons/area_tecnologia.png'),          // Tecnologia
    require('../../../../assets/icons/area_engenharias.png'),         // Engenharias
    require('../../../../assets/icons/simbolo_engenharia_producao.png'), // Eng. de Produção
    require('../../../../assets/icons/simbolo_caduceu.png'),          // Saúde (2º símbolo)
    require('../../../../assets/icons/area_direito.png'),             // Direito e Segurança
    require('../../../../assets/icons/area_negocios.png'),            // Negócios/Gestão
];

/**
 * MemoryCard - Horizontal full-width card for Resolva Questões
 * Premium black gradient with white bold fonts
 * Navigates to Questions screen
 */
const MemoryCard = memo(({ onPress }) => (
    <TouchableOpacity
        style={styles.cardContainer}
        onPress={onPress}
        activeOpacity={0.9}
    >
        <LinearGradient
            // Paleta derivada da cor-base #182472 (hue 235, sat 65%): mesma matiz
            // em 3 luminosidades — canto claro, base no meio, canto profundo.
            colors={['#0E1343', '#182472', '#0A0D2E']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.card}
        >
            {/* Header row with title and description */}
            <View style={styles.headerRow}>
                <View style={styles.textContent}>
                    <Text style={styles.title}>Resolva Questões</Text>
                    {/* `numberOfLines={1}` trava o requisito de linha única: se
                        a lista de exames crescer, o texto corta em vez de
                        empurrar a fileira de ícones para fora do card — e o
                        corte é o sinal de que a lista passou do limite.
                        Medido: com estes 3 exames sobra folga em 360px, a menor
                        largura de celular comum. */}
                    <Text style={styles.description} numberOfLines={1}>
                        Prepare-se para exames do ENADE · OAB · ENAMED
                    </Text>
                </View>
            </View>

            {/* Símbolos de curso, em fileira única */}
            <View style={styles.memoryGrid}>
                {ROW_ICONS.slice(0, 8).map((icon, i) => (
                    <View key={i} style={styles.miniCard}>
                        <Image source={icon} style={styles.bancaIcon} contentFit="contain" />
                    </View>
                ))}
                {/* Ver mais button */}
                <View style={[styles.miniCard, styles.verMaisCard]}>
                    <Text style={styles.verMaisText}>+</Text>
                </View>
            </View>
        </LinearGradient>
    </TouchableOpacity>
));

const styles = StyleSheet.create({
    cardContainer: {
        borderRadius: CARD_RADIUS.large,
        overflow: 'hidden',
        shadowColor: '#000',
        ...CARD_SHADOW.large,
    },

    card: {
        padding: CARD_PADDING.large,
        borderRadius: CARD_RADIUS.medium,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },

    headerRow: {
        marginBottom: CARD_PADDING.medium,
    },

    textContent: {
        flex: 1,
    },

    title: {
        fontSize: CARD_FONTS.titleLarge,
        fontWeight: '800',
        color: '#FFFFFF',
        marginBottom: 4,
        textShadowColor: 'rgba(0, 0, 0, 0.3)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
    },

    description: {
        fontSize: CARD_FONTS.description,
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.8)',
        lineHeight: CARD_FONTS.description * 1.4,
    },

    memoryGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },

    miniCard: {
        width: 36,
        height: 36,
        borderRadius: CARD_RADIUS.small,
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },

    bancaIcon: {
        width: 22,
        height: 22,
    },

    verMaisCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.25)',
        borderColor: 'rgba(255, 255, 255, 0.3)',
    },

    verMaisText: {
        fontSize: 18,
        fontWeight: '600',
        color: '#FFFFFF',
    },

    ctaLink: {
        fontSize: 15,
        fontWeight: '800',
        color: '#FFFFFF',
        marginTop: 'auto',
        textShadowColor: 'rgba(0, 0, 0, 0.3)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 2,
    },
});

export default MemoryCard;
